import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppState } from '../hooks/useAppState'

interface Result {
  type: 'project' | 'client' | 'expense' | 'sub' | 'vendor' | 'bid' | 'reminder'
  id: number
  label: string
  sub?: string
  path: string
}

const TYPE_ICON: Record<Result['type'], string> = {
  project: '📋', client: '👤', expense: '💰', sub: '🔨', vendor: '🏪', bid: '📨', reminder: '⏰',
}

export interface SearchBarHandle {
  focus: () => void
}

const SearchBar = forwardRef<SearchBarHandle>(function SearchBar(_, handleRef) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const { data } = useAppState()
  const navigate = useNavigate()
  const ref = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(handleRef, () => ({
    focus: () => {
      inputRef.current?.focus()
      inputRef.current?.select()
    },
  }), [])

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const q = query.trim().toLowerCase()
  const results: Result[] = []

  if (q.length >= 1 && data) {
    data.projects
      .filter((p) => p.name.toLowerCase().includes(q))
      .slice(0, 5)
      .forEach((p) =>
        results.push({ type: 'project', id: p.id, label: p.name, sub: p.status, path: `/projects?id=${p.id}` }),
      )
    ;(data.clients ?? [])
      .filter((c) => c.name.toLowerCase().includes(q))
      .slice(0, 3)
      .forEach((c) => {
        const sub = c.phone ?? c.email
        results.push({ type: 'client', id: c.id, label: c.name, path: '/clients', ...(sub ? { sub } : {}) })
      })
    data.expenses
      .filter(
        (e) =>
          (e.whoName ?? '').toLowerCase().includes(q) ||
          (e.cat ?? '').toLowerCase().includes(q) ||
          (e.notes ?? '').toLowerCase().includes(q),
      )
      .slice(0, 3)
      .forEach((e) =>
        results.push({
          type: 'expense',
          id: e.id,
          label: `$${e.amount.toLocaleString()} — ${e.whoName || 'Expense'}`,
          sub: e.date,
          path: '/expenses',
        }),
      )
    ;(data.subs ?? [])
      .filter((s) => s.name.toLowerCase().includes(q))
      .slice(0, 2)
      .forEach((s) =>
        results.push({ type: 'sub', id: s.id, label: s.name, path: '/subs', ...(s.field ? { sub: s.field } : {}) }),
      )
    // V1 parity: vendors, bids, reminders
    ;(data.vendors ?? [])
      .filter(
        (v) =>
          v.name.toLowerCase().includes(q) ||
          (v.cat ?? '').toLowerCase().includes(q) ||
          (v.contact ?? '').toLowerCase().includes(q),
      )
      .slice(0, 3)
      .forEach((v) => {
        const sub = v.cat ?? v.contact
        results.push({ type: 'vendor', id: v.id, label: v.name, path: '/vendors', ...(sub ? { sub } : {}) })
      })
    ;(data.bids ?? [])
      .filter(
        (b) =>
          (b.name ?? '').toLowerCase().includes(q) ||
          (b.cat ?? '').toLowerCase().includes(q) ||
          (b.notes ?? '').toLowerCase().includes(q),
      )
      .slice(0, 3)
      .forEach((b) => {
        const label = b.name || b.cat || (b.amount != null ? `$${b.amount.toLocaleString()}` : 'Bid')
        const sub = b.status ?? b.cat ?? (b.date ?? undefined)
        results.push({ type: 'bid', id: b.id, label, path: '/bidroom', ...(sub ? { sub } : {}) })
      })
    ;(data.reminders ?? [])
      .filter((r) => (r.text ?? '').toLowerCase().includes(q))
      .slice(0, 3)
      .forEach((r) =>
        results.push({
          type: 'reminder',
          id: r.id,
          label: r.text,
          path: '/',
          ...(r.date ? { sub: r.date } : {}),
        }),
      )
  }

  function go(r: Result) {
    navigate(r.path)
    setQuery('')
    setOpen(false)
  }

  return (
    <div ref={ref} style={{ flex: 1, maxWidth: 340, margin: '0 12px', position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Escape') { setQuery(''); setOpen(false); e.currentTarget.blur() } }}
        placeholder="Search projects, clients, expenses…"
        style={{
          width: '100%', fontSize: 12, padding: '7px 12px 7px 32px',
          borderRadius: 20, background: 'var(--bg)', border: '1px solid var(--border)',
          color: 'var(--text)', outline: 'none', transition: 'border-color .2s',
        }}
        onFocusCapture={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
        onBlurCapture={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
      />
      {/* Search icon */}
      <svg
        style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', opacity: 0.4 }}
        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
      >
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>

      {/* Results dropdown */}
      {open && q.length >= 1 && (
        <div className="search-results">
          {results.length > 0 ? (
            results.map((r, i) => (
              <div
                key={i}
                className="search-result-item"
                onClick={() => go(r)}
              >
                <span style={{ fontSize: 14 }}>{TYPE_ICON[r.type]}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.label}
                  </div>
                  {r.sub && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.sub}</div>}
                </div>
                <span style={{ fontSize: 10, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '.05em', flexShrink: 0 }}>
                  {r.type}
                </span>
              </div>
            ))
          ) : (
            <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-muted)' }}>
              No results for &ldquo;{query}&rdquo;
            </div>
          )}
        </div>
      )}
    </div>
  )
})

export default SearchBar
